import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Building2,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  Calendar,
  Users,
  MapPin,
  Briefcase,
  Target,
  Warehouse
} from 'lucide-react';
import { CompanyInfo, BUSINESS_SECTORS, TURKISH_CITIES } from '../../types/companyProfile';

interface CompanyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: CompanyInfo;
  onSave: (data: CompanyInfo) => void;
}

const COMPANY_TYPES = [
  { value: 'sahis', label: 'Şahıs Şirketi' },
  { value: 'limited', label: 'Limited Şirket' },
  { value: 'anonim', label: 'Anonim Şirket' },
  { value: 'kooperatif', label: 'Kooperatif' },
  { value: 'diger', label: 'Diğer' }
];

const TARGET_AUDIENCES = [
  { value: 'b2b', label: 'B2B (İşletmeden İşletmeye)', description: 'Diğer şirketlere satış yapıyorsunuz' },
  { value: 'b2c', label: 'B2C (İşletmeden Tüketiciye)', description: 'Son kullanıcılara satış yapıyorsunuz' },
  { value: 'b2b2c', label: 'B2B2C (Her İkisi)', description: 'Hem şirketlere hem tüketicilere satış yapıyorsunuz' }
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Yapay Zeka öğrenmeye başladı...', subtext: 'Şirketinizi tanımaya başlıyoruz' },
  { threshold: 3, message: 'AI analiz ediyor...', subtext: 'Sektörünüze göre öneriler hazırlanıyor' },
  { threshold: 5, message: 'Veriler işleniyor...', subtext: 'Lokasyon bazlı optimizasyonlar' },
  { threshold: 7, message: 'Model güncelleniyor...', subtext: 'AI size özel çözümler üretiyor' },
  { threshold: 9, message: 'Harika! Profil tamamlanıyor...', subtext: 'En iyi öneriler için hazırız' }
];

const STEPS = [
  { id: 'basic', title: 'Temel Bilgiler', icon: Building2 },
  { id: 'details', title: 'Şirket Detayları', icon: Briefcase },
  { id: 'location', title: 'Lokasyon', icon: MapPin },
  { id: 'warehouse', title: 'Depo Bilgileri', icon: Warehouse }
];

const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<CompanyInfo>(initialData || {});
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.companyName) count++;
    if (formData.foundedDate) count++;
    if (formData.companyType) count++;
    if (formData.employeeCount) count++;
    if (formData.sector) count++;
    if (formData.targetAudience) count++;
    if (formData.country) count++;
    if (formData.city) count++;
    if (formData.address) count++;
    if (formData.warehouseLocations && formData.warehouseLocations.length > 0) count++;
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const handleInputChange = (field: keyof CompanyInfo, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addWarehouse = () => {
    const current = formData.warehouseLocations || [];
    handleInputChange('warehouseLocations', [...current, { city: '', count: 1 }]);
  };

  const updateWarehouse = (index: number, field: 'city' | 'count', value: string | number) => {
    const current = formData.warehouseLocations || [];
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value };
    handleInputChange('warehouseLocations', updated);
  };

  const removeWarehouse = (index: number) => {
    const current = formData.warehouseLocations || [];
    handleInputChange('warehouseLocations', current.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSave(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Şirket Bilgileri</h2>
                <p className="text-sm text-gray-400">Adım {currentStep + 1} / {STEPS.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    index === currentStep
                      ? 'bg-blue-500/20 border border-blue-500/50'
                      : index < currentStep
                        ? 'bg-green-500/10 border border-green-500/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <StepIcon className={`w-4 h-4 ${
                    index === currentStep
                      ? 'text-blue-400'
                      : index < currentStep
                        ? 'text-green-400'
                        : 'text-gray-500'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    index === currentStep
                      ? 'text-blue-300'
                      : index < currentStep
                        ? 'text-green-300'
                        : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 10</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {/* Step 1: Basic Info */}
          {currentStep === 0 && (
            <div className="space-y-5">
              {/* Company Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  Şirket / Marka Adı
                  <span className="text-red-400 text-xs">*</span>
                </label>
                <input
                  type="text"
                  value={formData.companyName || ''}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Örn: MindBrush Sanat"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                />
              </div>

              {/* Founded Date */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  Kuruluş Tarihi
                </label>
                <input
                  type="date"
                  value={formData.foundedDate || ''}
                  onChange={(e) => handleInputChange('foundedDate', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                />
              </div>

              {/* Company Type */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Briefcase className="w-4 h-4 text-blue-400" />
                  Şirket Türü
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {COMPANY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleInputChange('companyType', type.value)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        formData.companyType === type.value
                          ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-300'
                          : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Employee Count */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Çalışan Sayısı
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.employeeCount || ''}
                  onChange={(e) => handleInputChange('employeeCount', parseInt(e.target.value) || undefined)}
                  placeholder="Örn: 25"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                />
              </div>
            </div>
          )}

          {/* Step 2: Company Details */}
          {currentStep === 1 && (
            <div className="space-y-5">
              {/* Sector */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Briefcase className="w-4 h-4 text-purple-400" />
                  Sektör
                </label>
                <select
                  value={formData.sector || ''}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a1a2e]">Sektör seçin</option>
                  {BUSINESS_SECTORS.map((sector) => (
                    <option key={sector} value={sector} className="bg-[#1a1a2e]">
                      {sector}
                    </option>
                  ))}
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Target className="w-4 h-4 text-purple-400" />
                  Hedef Kitle
                </label>
                <div className="space-y-2">
                  {TARGET_AUDIENCES.map((audience) => (
                    <button
                      key={audience.value}
                      type="button"
                      onClick={() => handleInputChange('targetAudience', audience.value)}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        formData.targetAudience === audience.value
                          ? 'bg-purple-500/20 border-2 border-purple-500'
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${
                            formData.targetAudience === audience.value ? 'text-purple-300' : 'text-white'
                          }`}>
                            {audience.label}
                          </p>
                          <p className="text-sm text-gray-400 mt-0.5">{audience.description}</p>
                        </div>
                        {formData.targetAudience === audience.value && (
                          <Check className="w-5 h-5 text-purple-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {currentStep === 2 && (
            <div className="space-y-5">
              {/* Country */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  Ülke
                </label>
                <select
                  value={formData.country || ''}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a1a2e]">Ülke seçin</option>
                  <option value="Türkiye" className="bg-[#1a1a2e]">Türkiye</option>
                  <option value="ABD" className="bg-[#1a1a2e]">ABD</option>
                  <option value="Almanya" className="bg-[#1a1a2e]">Almanya</option>
                  <option value="İngiltere" className="bg-[#1a1a2e]">İngiltere</option>
                  <option value="Diğer" className="bg-[#1a1a2e]">Diğer</option>
                </select>
              </div>

              {/* City */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  Şehir
                </label>
                <select
                  value={formData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a1a2e]">Şehir seçin</option>
                  {TURKISH_CITIES.map((city) => (
                    <option key={city} value={city} className="bg-[#1a1a2e]">
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              {/* Address */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  Adres
                </label>
                <textarea
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Merkez ofis adresiniz"
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 4: Warehouse */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <Warehouse className="w-4 h-4 text-orange-400" />
                  Depo Lokasyonları
                </label>
                <button
                  type="button"
                  onClick={addWarehouse}
                  className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg text-sm font-medium hover:bg-orange-500/30 transition-colors"
                >
                  + Depo Ekle
                </button>
              </div>

              {(!formData.warehouseLocations || formData.warehouseLocations.length === 0) && (
                <div className="text-center py-8 bg-white/5 rounded-xl border border-dashed border-white/10">
                  <Warehouse className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">Henüz depo eklenmedi</p>
                  <p className="text-sm text-gray-500 mt-1">Depo bilgileri AI&apos;ın lojistik önerilerini iyileştirir</p>
                </div>
              )}

              <div className="space-y-3">
                {formData.warehouseLocations?.map((warehouse, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                    <select
                      value={warehouse.city}
                      onChange={(e) => updateWarehouse(index, 'city', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-orange-500/50 transition-all appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#1a1a2e]">Şehir seçin</option>
                      {TURKISH_CITIES.map((city) => (
                        <option key={city} value={city} className="bg-[#1a1a2e]">
                          {city}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={warehouse.count}
                      onChange={(e) => updateWarehouse(index, 'count', parseInt(e.target.value) || 1)}
                      className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center focus:border-orange-500/50 transition-all"
                      placeholder="Adet"
                    />
                    <button
                      type="button"
                      onClick={() => removeWarehouse(index)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="w-4 h-4" />
                  Kaydet
                </>
              ) : (
                <>
                  İleri
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CompanyInfoModal;
